import { useState, useEffect, useCallback } from 'react'
import { AgentReferenceType } from '../store/schemas'
import { fetchAgentCaselawHtml } from '../fetch/fetchAgentCaselawHtml'
import { fetchAgentLrrHtml } from '../fetch/fetchAgentLrrHtml'

interface UseAgentReferenceHtmlState {
  htmlContent: string | null
  isLoading: boolean
  isError: boolean
  retry: () => void
}

/**
 * Hook to fetch HTML content for Agent Caselaw and LRR references
 *
 * @param type - The agent reference type
 * @param s3_path - The S3 directory of the agent reference
 * @returns Object containing HTML content, loading state, error state, and retry function
 */
export function useAgentReferenceHtml(type: AgentReferenceType, s3_path: string): UseAgentReferenceHtmlState {
  const [htmlContent, setHtmlContent] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setIsError(false)

    try {
      if (!s3_path) {
        throw new Error('No s3_path found in reference metadata')
      }

      // Fetch HTML based on reference type
      let result
      if (type === AgentReferenceType.CASELAW) {
        result = await fetchAgentCaselawHtml(s3_path)
      } else {
        result = await fetchAgentLrrHtml(s3_path)
      }

      if (!result || !result.data) {
        setIsError(true)
        setHtmlContent(null)
      } else {
        const htmlData = result.data
        setHtmlContent(htmlData)
        setIsError(false)
      }
    } catch (err) {
      console.error('Error fetching agent reference HTML:', err)
      setIsError(true)
      setHtmlContent(null)
    } finally {
      setIsLoading(false)
    }
  }, [type, s3_path])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    htmlContent,
    isLoading,
    isError,
    retry: fetchData,
  }
}
